import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Timestamp, addDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuListChecks, LuLoaderCircle, LuPlus, LuSend, LuWallet } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { Field, SelectInput, TextArea, TextInput } from '../../../components/form/FormControls';
import { auth, db, storage } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD, HelpNote, SectionHeader } from '../components/ProfileUi';
import PdfPicker from './PdfPicker';
import { DEFAULT_EXPENSE_CATEGORY, EXPENSE_CATEGORIES } from './expenseOptions';

/**
 * "Masraf Talep Et": an expense with an amount, a category, a description
 * and optional PDF receipts. It is saved for approval; the finance managers
 * get a task and an e-mail.
 */
const ExpenseRequestForm = ({ userData, employeeData, employeeId, onShowTracking }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.expense.form.${key}`, vars);
  const [expenseSubmitted, setExpenseSubmitted] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState(DEFAULT_EXPENSE_CATEGORY);
  const [expenseDescription, setExpenseDescription] = useState('');
  const [expensePdfFiles, setExpensePdfFiles] = useState([]);
  const [expensePdfUploading, setExpensePdfUploading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expenseErrors, setExpenseErrors] = useState({});

  const handleExpenseRequestSubmit = async () => {
    const errors = {};
    if (!expenseAmount) errors.amount = label('errors.amountRequired');
    else if (parseFloat(expenseAmount) <= 0) errors.amount = label('errors.amountPositive');
    if (!expenseDescription) errors.description = label('errors.descriptionRequired');
    setExpenseErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error(label('errors.required'));
      return;
    }

    const eName = userData.eName || employeeData?.eName || '';
    const eSurname = userData.eSurname || employeeData?.eSurname || '';
    const eMail = userData.eMail || employeeData?.eMail || auth.currentUser?.email || '';
    if (!eName || !eSurname) {
      toast.error(label('errors.user'));
      return;
    }

    setExpenseLoading(true);
    try {
      let pdfUrls = [];
      let pdfFileNames = [];
      if (expensePdfFiles.length > 0) {
        setExpensePdfUploading(true);
        const timestamp = Date.now();
        try {
          const uploadResults = await Promise.all(expensePdfFiles.map(async (file, index) => {
            const fileName = `expense-${employeeId}-${timestamp}-${index + 1}.pdf`;
            const snapshot = await uploadBytes(ref(storage, `expense-documents/${fileName}`), file);
            const url = await getDownloadURL(snapshot.ref);
            return { url, fileName: file.name };
          }));
          pdfUrls = uploadResults.map((result) => result.url);
          pdfFileNames = uploadResults.map((result) => result.fileName);
        } catch (uploadError) {
          console.error('❌ PDF upload error:', uploadError);
          toast.error(label('errors.upload'));
          setExpensePdfUploading(false);
          setExpenseLoading(false);
          return;
        }
        setExpensePdfUploading(false);
      }

      const docRef = await addDoc(collection(db, 'ExpenseRequests'), {
        employeeId,
        employeeName: `${eName} ${eSurname}`,
        employeeEmail: eMail,
        amount: parseFloat(expenseAmount),
        category: expenseCategory,
        description: expenseDescription,
        pdfUrls,
        pdfFileNames,
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        requestType: 'EXPENSE_REQUEST',
      });

      // Aktif "Finans Yöneticisi" yetkililerine görev (yetki dizi ya da metin olabilir)
      try {
        const employeesSnap = await getDocs(collection(db, 'Employees'));
        const hasFinanceRole = (authorization) => {
          if (!authorization) return false;
          if (Array.isArray(authorization)) return authorization.includes('Finans Yöneticisi');
          return authorization === 'Finans Yöneticisi';
        };
        const financeAssignees = employeesSnap.docs
          .filter((d) => hasFinanceRole(d.data().authorization) && d.data().Active === 1)
          .map((d) => {
            const data = d.data();
            return { id: d.id, name: `${data.eName || ''} ${data.eSurname || ''}`.trim() || (data.eMail || 'Finans Yöneticisi') };
          });

        if (financeAssignees.length > 0) {
          const requesterName = `${eName || ''} ${eSurname || ''}`.trim() || (eMail || 'Bir çalışan');
          const amountText = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(expenseAmount));
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 3);
          await addDoc(collection(db, 'Tasks'), {
            title: `Cevaplanması gereken bir masraf talebi bulunmakta — ${requesterName}`,
            description: `${requesterName} tarafından ${amountText} ₺ tutarında bir masraf talebi oluşturuldu.\n\nKategori: ${expenseCategory}\nAçıklama: ${expenseDescription}\n\nLütfen talebi inceleyip onaylayın veya reddedin.`,
            priority: 'medium',
            status: 'pending',
            assignees: financeAssignees,
            assigneeName: financeAssignees.map((a) => a.name).join(', '),
            assigneeId: financeAssignees.length === 1 ? financeAssignees[0].id : null,
            dueDate: Timestamp.fromDate(dueDate),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: requesterName,
            source: 'EXPENSE_REQUEST',
            relatedExpenseRequestId: docRef.id,
            viewedBy: [],
          });
        } else {
          console.warn('⚠️ No Finans Yöneticisi found — task not created');
        }
      } catch (taskError) {
        console.error('❌ Finans task creation error:', taskError);
      }

      // Finans yöneticilerine bildirim e-postası (gönderilemese de talep kayıtlıdır)
      try {
        const response = await fetch('https://us-central1-habcare-8ecad.cloudfunctions.net/sendExpenseRequestNotification', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            employeeName: `${eName} ${eSurname}`,
            employeeEmail: eMail,
            amount: parseFloat(expenseAmount),
            category: expenseCategory,
            description: expenseDescription,
          }),
        });
        if (!response.ok) console.error('❌ Masraf bildirim maili gönderilemedi:', response.status);
      } catch (mailError) {
        console.error('❌ Masraf bildirim maili hatası:', mailError);
      }

      setExpenseAmount('');
      setExpenseCategory(DEFAULT_EXPENSE_CATEGORY);
      setExpenseDescription('');
      setExpensePdfFiles([]);
      setExpenseErrors({});
      setExpenseSubmitted(true);
    } catch (error) {
      console.error('❌ Masraf talebi gönderme hatası:', error);
      toast.error(label('errors.send', { message: error.message }));
    } finally {
      setExpenseLoading(false);
      setExpensePdfUploading(false);
    }
  };

  if (expenseSubmitted) {
    return (
      <div className={`${CARD} tw-flex tw-flex-col tw-items-center tw-gap-4 tw-px-6 tw-py-12 tw-text-center`}>
        <GlassIcon gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" size="md">
          <LuCircleCheck aria-hidden="true" className="tw-h-6 tw-w-6" />
        </GlassIcon>
        <div className="tw-space-y-1">
          <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('successTitle')}</h2>
          <p className="tw-mx-auto tw-max-w-md tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('successText')}</p>
        </div>
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
          <Button onClick={onShowTracking}>
            <LuListChecks aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('goTracking')}
          </Button>
          <Button variant="primary" onClick={() => setExpenseSubmitted(false)}>
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('newRequest')}
          </Button>
        </div>
      </div>
    );
  }

  const busy = expenseLoading || expensePdfUploading;

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuWallet}
        gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30"
        title={label('title')}
        description={label('description')}
      />

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleExpenseRequestSubmit();
        }}
        className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}
      >
        <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
          <Field label={label('amount')} htmlFor="expense-amount" required error={expenseErrors.amount}>
            <div className="tw-relative">
              <span aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-sm tw-font-semibold tw-text-slate-400">₺</span>
              <TextInput
                id="expense-amount"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={expenseAmount}
                onChange={(event) => {
                  setExpenseAmount(event.target.value);
                  setExpenseErrors((prev) => ({ ...prev, amount: undefined }));
                }}
                placeholder="0.00"
                error={expenseErrors.amount}
                className="tw-pl-8"
              />
            </div>
          </Field>
          <Field label={label('category')} htmlFor="expense-category" required>
            <SelectInput
              id="expense-category"
              value={expenseCategory}
              current={expenseCategory}
              onChange={(event) => setExpenseCategory(event.target.value)}
              options={EXPENSE_CATEGORIES.map((category) => ({ value: category.value, label: t(`profilePage.expense.categories.${category.key}`) }))}
            />
          </Field>
          <Field label={label('descriptionLabel')} htmlFor="expense-description" required error={expenseErrors.description} className="sm:tw-col-span-2">
            <TextArea
              id="expense-description"
              rows={4}
              value={expenseDescription}
              onChange={(event) => {
                setExpenseDescription(event.target.value);
                setExpenseErrors((prev) => ({ ...prev, description: undefined }));
              }}
              placeholder={label('descriptionPlaceholder')}
              error={expenseErrors.description}
            />
          </Field>
          <Field label={label('documents')} htmlFor="expense-pdf-input" hint={label('documentsHint')} className="sm:tw-col-span-2">
            <PdfPicker id="expense-pdf-input" files={expensePdfFiles} onChange={setExpensePdfFiles} disabled={busy} />
          </Field>
        </div>

        <HelpNote>{label('approvalNote')}</HelpNote>

        <div className="tw-flex tw-justify-end">
          <Button variant="primary" type="submit" disabled={busy} className="max-sm:tw-w-full">
            {busy ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSend aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {expensePdfUploading ? label('uploading') : expenseLoading ? label('sending') : label('submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};

ExpenseRequestForm.propTypes = {
  userData: PropTypes.object.isRequired,
  employeeData: PropTypes.object,
  employeeId: PropTypes.string,
  onShowTracking: PropTypes.func.isRequired,
};

export default ExpenseRequestForm;
